// client/src/components/register/RegisterForm.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import { generateSecurePassword } from '../../utils/passwordGenerator';
import { register } from '../../api/authApi';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // Import icons

import 'react-phone-input-2/lib/style.css';
import './RegisterForm.css';

function RegisterForm() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('email');
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState('');
    const [showPassword, setShowPassword] = useState(false); // State for visibility

    const [usernameError, setUsernameError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [registrationError, setRegistrationError] = useState('');


    const handleUsernameChange = (event) => {
        setUsername(event.target.value);
        setUsernameError('');
    };

    const handlePhoneChange = (value, country) => {
        setPhone(value);
        setCountryCode(country.dialCode);
        setPhoneError('');
    };

    const handlePasswordChange = (event) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        setPasswordError('');
        setPasswordStrength(calculatePasswordStrength(newPassword));
    };

    const handleConfirmPasswordChange = (event) => {
        setConfirmPassword(event.target.value);
        setConfirmPasswordError('');
    };



    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        setPassword(newPassword);
        setConfirmPassword(newPassword);
        setPasswordError('');
        setPasswordStrength(calculatePasswordStrength(newPassword));
        setShowPassword(true); // Show the generated password. IMPORTANT
    };

    const calculatePasswordStrength = (password) => {
        if (password.length === 0) {
            return '';
        }
        if (password.length < 8) {
            return 'weak';
        }
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++; // Uppercase
        if (/[a-z]/.test(password)) score++; // Lowercase
        if (/[0-9]/.test(password)) score++; // Digit
        if (/[^A-Za-z0-9]/.test(password)) score++; // Special character

        if (score <= 2) return 'weak';
        if (score <= 4) return 'medium';
        return 'strong';
    };



    const handleSubmit = async (event) => {
        event.preventDefault();
        setRegistrationError('');

        let hasError = false;
        let identifierField = '';
        let identifierValue = '';
        let finalUsername = '';
        let phoneValueForApi = phone;

        // --- Phone Number Formatting Logic ---
        if (activeTab === 'phone') {
            let formattedPhoneNumber = phone; // Start with the raw input
            // Ensure phone and countryCode are available and phone starts with countryCode
            if (phone && countryCode && phone.startsWith(countryCode)) {
                const nationalNumber = phone.substring(countryCode.length); // Get part after country code
                // Check if the national part starts with '0' and has more digits after it
                if (nationalNumber.startsWith('0') && nationalNumber.length > 1) {
                    formattedPhoneNumber = countryCode + nationalNumber.substring(1); // Remove leading '0'
                    console.log(`Formatted phone: Raw=${phone}, National=${nationalNumber}, Formatted=${formattedPhoneNumber}`);
                } else {
                    // Log even if no formatting is needed, helps debugging
                     console.log(`Phone formatting not needed or invalid national part: Raw=${phone}, National=${nationalNumber}`);
                }
            } else {
                console.log(`Phone formatting skipped: phone=${phone}, countryCode=${countryCode}`);
            }
            phoneValueForApi = formattedPhoneNumber; // Use the potentially formatted number for the API
        }

        if (activeTab === 'email') {
            if (!username) {
                setUsernameError(t('Register.errors.emailRequired'));
                hasError = true;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)) {
                setUsernameError(t('Register.errors.invalidEmail'));
                hasError = true;
            }
            identifierField = 'email';
            identifierValue = username;
            finalUsername = username.split('@')[0];
        } else {
            if (!phone) {
                setPhoneError(t('Register.errors.phoneNumberRequired'));
                hasError = true;
            } else if (phone.length < 5) {
                setPhoneError(t('Register.errors.invalidPhoneNumber'));
                hasError = true;
            }
            identifierField = 'phone_number';
            identifierValue = phoneValueForApi;
            finalUsername = phoneValueForApi;
        }

        if (!password) {
            setPasswordError(t('Register.errors.passwordRequired'));
            hasError = true;
        } else if (password.length < 8) {
            setPasswordError(t('Register.errors.passwordTooShort'));
            hasError = true;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(t('Register.errors.passwordsDontMatch'));
            hasError = true;
        }

        if (hasError) {
            return;
        }

        const registrationData = {
            username: finalUsername,
            [identifierField]: identifierValue,
            password: password,
            language: i18n.language,
        };

        // --- API Call and Response Handling ---
        try {
            // 'response' will contain the data from backend on success (axios puts it in .data)
            const response = await register(registrationData);

            // --- SUCCESS HANDLING ---
            console.log("Registration successful:", response); // Log success response
            // Check for expected data in the success response
            if (response && response.userId) {
                // Optional: Store session token if backend provides it on registration
                // if (response.sessionToken) {
                //    sessionStorage.setItem('sessionToken', response.sessionToken);
                // }
                // Navigate to activation page
                navigate(`/activate/${response.userId}`);
            } else {
                 // Handle cases where success response is missing expected data
                 console.error('Registration success response missing userId:', response);
                 setRegistrationError('Registration response invalid. Please contact support.'); // Or use a translation key
            }

        } catch (error) {
            // --- ERROR HANDLING ---
            // Errors from handleApiError (axios non-2xx status) are caught here
            console.error('Error during registration (caught in component):', error);
            // error.message should contain the translation key or backend message
            setRegistrationError(error.message || 'An unexpected error occurred.'); // Display the error message/key
        }
    };

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };


  return (
    <div className="register-form">
      <h2 className="register-form__title">{t('Register.registerTitle')}</h2>

      <form onSubmit={handleSubmit}>
        {registrationError && <div className="register-form__error">{t(registrationError)}</div>}
          {/* Tabs as Labels */}
          <div className="register-form__group">
            <div className="register-form__tabs">
                <span
                    className={`register-form__tab-label ${activeTab === 'email' ? 'register-form__tab-label--active' : ''}`}
                    onClick={() => setActiveTab('email')}
                >
                    {t('Register.emailLabel')}
                </span>
                <span className="register-form__tab-separator">|</span>
                <span
                    className={`register-form__tab-label ${activeTab === 'phone' ? 'register-form__tab-label--active' : ''}`}
                    onClick={() => setActiveTab('phone')}
                >
                    {t('Register.phoneNumberLabel')}
                </span>
            </div>
            {/* Conditionally render input based on activeTab */}
            {activeTab === 'email' && (
                <div className="register-form__input-wrapper">
                    <input
                        type="email"
                        id="username"
                        name="username"
                        value={username}
                        onChange={handleUsernameChange}
                        placeholder={t('Register.emailPlaceholder')}
                        className={`register-form__input ${usernameError ? 'register-form__input--error' : ''}`}
                    />
                    {/* Username Error */}
                    {usernameError && <div className="register-form__error">{t(usernameError)}</div>}
                </div>
            )}

            {activeTab === 'phone' && (
            <div className="register-form__input-wrapper register-form__phone-input-wrapper">
                <PhoneInput
                    country={'dz'}
                    value={phone}
                    onChange={handlePhoneChange}
                    enableSearch={true}
                    countryCodeEditable={false}
                    specialLabel=""
                    inputProps={{
                        name: 'phone',
                        id: 'phone',
                    }}
                    containerClass="register-form__phone-container"
                    inputClass={`register-form__phone-number-input ${phoneError ? 'register-form__input--error' : ''}`}
                    placeholder={t('Register.phoneNumberPlaceholder')}
                />
                {/* Phone Error */}
                {phoneError && <div className="register-form__error">{t(phoneError)}</div>}
                </div>
            )}
            </div>

            <div className="register-form__group">
                <label htmlFor="password" className="register-form__label">{t('Register.passwordLabel')}</label>
                <div className="register-form__password-input-wrapper">
                  <input
                      type={showPassword ? 'text' : 'password'} // Toggle input type
                      id="password"
                      name="password"
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder={t('Register.passwordPlaceholder')}
                      className={`register-form__input ${passwordError ? 'register-form__input--error' : ''}`}
                  />
                    {/* Show/Hide Password Toggle */}
                    <button
                        type="button"
                        className="register-form__toggle-password-button"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? t('Register.hidePassword') : t('Register.showPassword')}
                    >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                    {/* Icon Button */}
                  <button
                      type="button"
                      className="register-form__generate-password-button"
                      onClick={handleGeneratePassword}
                      aria-label={t('Register.generatePassword')}
                  >
                      <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
                {/* Password Error */}
                {passwordError && <div className="register-form__error">{t(passwordError)}</div>}
                {/* Password Strength Indicator - NO TEXT */}
                {password && (
                    <div className="register-form__password-strength">
                        <div className={`password-strength-bar password-strength-${passwordStrength}`}>
                        </div>

                    </div>
                )}
            </div>

            <div className="register-form__group">
                <label htmlFor="confirmPassword" className="register-form__label">{t('Register.confirmPasswordLabel')}</label>
                    <input
                    type="password" // ALWAYS password type
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder={t('Register.confirmPasswordPlaceholder')}
                    className={`register-form__input ${confirmPasswordError ? 'register-form__input--error' : ''}`}
                    />
                {/* Confirm Password Error */}
                {confirmPasswordError && <div className="register-form__error">{t(confirmPasswordError)}</div>}
            </div>

            {/* Agreement Section -  removed checkbox*/}
            <div className="register-form__group register-form__terms">
                <div>
                    <p className="register-form__terms-text">
                        {t('Register.agreeTermsNewPrefix')}
                         <Link to="/privacy-policy" className="register-form__link">
                            {t('Register.privacyPolicy')}
                        </Link>,&nbsp;
                        <Link to="/cookies-policy" className="register-form__link">
                            {t('Register.cookiesPolicy')}
                        </Link>,&nbsp;
                        {t('Register.and')}&nbsp;
                        <Link to="/user-agreement" className="register-form__link">
                        {t('Register.userAgreement')}</Link>.
                    </p>
                </div>
            </div>

            <button type="submit" className="register-form__button">{t('Register.Button')}</button>

            <div className="register-form__login-link">
            {t('Register.alreadyHaveAccount')} <Link to="/login">{t('Register.loginTitle')}</Link>
            </div>
        </form>
    </div>
  );
}

export default RegisterForm;